import React from "react";
import { useSearchParams } from "react-router-dom";

export type ResourceStatus = "idle" | "loading" | "ready" | "error";

export interface Resource<T> {
  data: T | null;
  status: ResourceStatus;
  error: string | null;
  /** Load again; keeps the current data on screen while it runs */
  reload: () => Promise<void>;
  setData: React.Dispatch<React.SetStateAction<T | null>>;
}

export const errorMessage = (err: any, fallback: string): string => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return String(detail[0].msg);
  return fallback;
};

/** Load data when `enabled`, with status, error and reload. `pollMs` refreshes
 * in the background while the tab is visible. */
export function useResource<T>(
  fetcher: () => Promise<T>,
  deps: React.DependencyList,
  options: { enabled?: boolean; pollMs?: number; errorText?: string } = {}
): Resource<T> {
  const { enabled = true, pollMs, errorText = "Couldn't load this." } = options;
  const [data, setData] = React.useState<T | null>(null);
  const [status, setStatus] = React.useState<ResourceStatus>("idle");
  const [error, setError] = React.useState<string | null>(null);
  const fetcherRef = React.useRef(fetcher);
  fetcherRef.current = fetcher;
  const seq = React.useRef(0);

  const reload = React.useCallback(async () => {
    const mine = ++seq.current;
    setStatus((s) => (s === "ready" ? "ready" : "loading"));
    try {
      const next = await fetcherRef.current();
      if (mine !== seq.current) return;
      setData(next);
      setError(null);
      setStatus("ready");
    } catch (err) {
      if (mine !== seq.current) return;
      setError(errorMessage(err, errorText));
      setStatus("error");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorText]);

  React.useEffect(() => {
    if (!enabled) return;
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, reload, ...deps]);

  React.useEffect(() => {
    if (!enabled || !pollMs) return;
    const tick = () => {
      if (document.visibilityState === "visible") reload();
    };
    const id = window.setInterval(tick, pollMs);
    document.addEventListener("visibilitychange", tick);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [enabled, pollMs, reload]);

  return { data, status, error, reload, setData };
}

/** One search param as state, e.g. the record a dialog shows (`?order=<id>`),
 * so dialogs survive refresh, can be linked to, and ⌘K can open them.
 * Setting null removes the param. History entries are replaced, not pushed,
 * unless `push` is true (use push when opening, so Back closes the dialog). */
export function useUrlParam(key: string): [string | null, (value: string | null, push?: boolean) => void] {
  const [params, setParams] = useSearchParams();
  const value = params.get(key);
  const set = React.useCallback(
    (next: string | null, push = false) => {
      setParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (next === null || next === "") p.delete(key);
          else p.set(key, next);
          return p;
        },
        { replace: !push }
      );
    },
    [key, setParams]
  );
  return [value, set];
}

/** True below the given MUI breakpoint width (default md = 900px). */
export function useIsNarrow(px = 900): boolean {
  const query = `(max-width:${px - 0.05}px)`;
  const get = () => (typeof window !== "undefined" && window.matchMedia ? window.matchMedia(query).matches : false);
  const [narrow, setNarrow] = React.useState(get);
  React.useEffect(() => {
    if (!window.matchMedia) return;
    const mql = window.matchMedia(query);
    const on = () => setNarrow(mql.matches);
    on();
    mql.addEventListener?.("change", on);
    return () => mql.removeEventListener?.("change", on);
  }, [query]);
  return narrow;
}
